// src/pages/common/MaintenancePage.jsx
import React, { useEffect, useState } from "react";

export default function WithMaintenanceCheck({ children }) {
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    fetch("/api/health")
      .then(res => {
        if (res.status === 503) setIsMaintenance(true);
      })
      .catch(() => setIsMaintenance(true))
      .finally(() => setChecking(false));
  }, []);

  if (checking) return null;
  if (isMaintenance) return <MaintenanceUI />;
  return children;
}

function MaintenanceUI() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500&display=swap');

        .maint-body {
          min-height: 100vh;
          background: #EEF2FB;
          font-family: 'DM Sans', sans-serif;
          color: #1a2340;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          position: relative;
        }
        .maint-body::before {
          content: '';
          position: fixed;
          inset: 0;
          background-image: radial-gradient(circle, #C8D8F0 1.2px, transparent 1.2px);
          background-size: 28px 28px;
          opacity: 0.6;
          z-index: 0;
        }
        .maint-blob {
          position: fixed;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.25;
          z-index: 0;
          animation: maint-drift 12s ease-in-out infinite alternate;
        }
        .maint-blob-1 { width: 420px; height: 420px; background: #378ADD; top: -100px; left: -80px; }
        .maint-blob-2 { width: 320px; height: 320px; background: #1D9E75; bottom: -80px; right: -60px; animation-delay: 4s; }
        .maint-blob-3 { width: 200px; height: 200px; background: #7F77DD; top: 40%; right: 15%; animation-delay: 2s; }
        @keyframes maint-drift {
          from { transform: translate(0, 0) scale(1); }
          to   { transform: translate(20px, 15px) scale(1.06); }
        }
        .maint-card {
          position: relative;
          z-index: 1;
          background: #ffffff;
          border-radius: 32px;
          padding: 64px 72px 56px;
          max-width: 640px;
          width: calc(100% - 40px);
          border: 1px solid rgba(180, 210, 244, 0.6);
          box-shadow: 0 2px 48px rgba(55, 138, 221, 0.08);
          text-align: center;
          animation: maint-rise 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @keyframes maint-rise {
          from { opacity: 0; transform: translateY(32px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .maint-illustration {
          margin: 0 auto 36px;
          width: 200px;
          height: 200px;
          animation: maint-floatY 4s ease-in-out infinite;
        }
        @keyframes maint-floatY {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-10px); }
        }
        .maint-gear-spin     { animation: maint-spin 8s linear infinite; transform-origin: center; }
        .maint-gear-spin-rev { animation: maint-spin 6s linear infinite reverse; transform-origin: center; }
        @keyframes maint-spin { to { transform: rotate(360deg); } }
        .maint-pulse-ring {
          animation: maint-pulseRing 2s ease-out infinite;
          transform-origin: center;
        }
        @keyframes maint-pulseRing {
          0%   { opacity: 0.7; transform: scale(0.85); }
          100% { opacity: 0;   transform: scale(1.4); }
        }
        .maint-eyebrow {
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #378ADD;
          margin-bottom: 14px;
        }
        .maint-h1 {
          font-family: 'DM Serif Display', Georgia, serif;
          font-size: 42px;
          line-height: 1.15;
          color: #1a2340;
          margin-bottom: 16px;
        }
        .maint-h1 em { font-style: italic; color: #185FA5; }
        .maint-subtitle {
          font-size: 16px;
          font-weight: 300;
          line-height: 1.7;
          color: #6b7a9e;
          max-width: 400px;
          margin: 0 auto 40px;
        }
        .maint-status-bar {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: #EEF2FB;
          border-radius: 99px;
          padding: 10px 20px;
          font-size: 13px;
          font-weight: 500;
          color: #6b7a9e;
        }
        .maint-status-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: #EF9F27;
          position: relative;
          flex-shrink: 0;
        }
        .maint-status-dot::after {
          content: '';
          position: absolute;
          inset: -3px;
          border-radius: 50%;
          background: #EF9F27;
          opacity: 0.3;
          animation: maint-blink 1.8s ease-in-out infinite;
        }
        @keyframes maint-blink {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50%       { transform: scale(1.8); opacity: 0; }
        }
      `}</style>

      <div className="maint-body">
        <div className="maint-blob maint-blob-1" />
        <div className="maint-blob maint-blob-2" />
        <div className="maint-blob maint-blob-3" />

        <div className="maint-card">
          <div className="maint-illustration">
            <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Server cabinet */}
              <rect x="72" y="48" width="72" height="104" rx="6" fill="#D0DEF4" />
              <rect x="76" y="52" width="64" height="92" rx="4" fill="#1a2340" />
              <rect x="80" y="58" width="56" height="12" rx="2" fill="#2a3a5c" />
              <rect x="82" y="60" width="20" height="8" rx="1" fill="#0C447C" />
              <circle cx="107" cy="64" r="2" fill="#5DCAA5" />
              <circle cx="113" cy="64" r="2" fill="#5DCAA5" />
              <circle cx="119" cy="64" r="2" fill="#378ADD" />
              <circle cx="125" cy="64" r="2" fill="#EF9F27" />
              <rect x="80" y="74" width="56" height="12" rx="2" fill="#2a3a5c" />
              <rect x="82" y="76" width="20" height="8" rx="1" fill="#0C447C" />
              <circle cx="107" cy="80" r="2" fill="#5DCAA5" />
              <circle cx="113" cy="80" r="2" fill="#378ADD" />
              <circle cx="119" cy="80" r="2" fill="#378ADD" />
              <circle cx="125" cy="80" r="2" fill="#5DCAA5" />
              <rect x="80" y="90" width="56" height="12" rx="2" fill="#2a3a5c" />
              <rect x="82" y="92" width="20" height="8" rx="1" fill="#185FA5" />
              <circle cx="107" cy="96" r="2" fill="#EF9F27" />
              <circle cx="113" cy="96" r="2" fill="#EF9F27" />
              <circle cx="119" cy="96" r="2" fill="#5DCAA5" />
              <circle cx="125" cy="96" r="2" fill="#5DCAA5" />
              <rect x="80" y="106" width="56" height="12" rx="2" fill="#2a3a5c" />
              <rect x="82" y="108" width="20" height="8" rx="1" fill="#0C447C" />
              <circle cx="107" cy="112" r="2" fill="#378ADD" />
              <circle cx="113" cy="112" r="2" fill="#5DCAA5" />
              {/* Cables */}
              <path d="M 100 118 Q 92 130 88 148" stroke="#378ADD" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
              <path d="M 108 118 Q 108 135 104 148" stroke="#5DCAA5" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
              <path d="M 116 118 Q 120 132 116 148" stroke="#EF9F27" strokeWidth="2" strokeLinecap="round" fill="none"/>
              {/* Robot */}
              <ellipse cx="46" cy="148" rx="22" ry="16" fill="#B5D4F4" />
              <rect x="30" y="120" width="34" height="30" rx="8" fill="#D0DEF4" />
              <rect x="34" y="100" width="26" height="22" rx="8" fill="#E6F1FB" />
              <circle cx="42" cy="109" r="4" fill="#185FA5" />
              <circle cx="54" cy="109" r="4" fill="#185FA5" />
              <circle cx="43" cy="108" r="1.5" fill="#fff" />
              <circle cx="55" cy="108" r="1.5" fill="#fff" />
              <line x1="47" y1="100" x2="47" y2="92" stroke="#B5D4F4" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="47" cy="90" r="3" fill="#378ADD" />
              <path d="M 64 130 Q 72 126 75 122" stroke="#B5D4F4" strokeWidth="5" strokeLinecap="round" fill="none"/>
              <g transform="translate(73, 116) rotate(-30)">
                <rect x="-2" y="-8" width="4" height="14" rx="1" fill="#6b7a9e"/>
                <ellipse cx="0" cy="-9" rx="4" ry="3" fill="#6b7a9e"/>
              </g>
              {/* Toolbox */}
              <rect x="120" y="148" width="32" height="22" rx="4" fill="#185FA5" />
              <rect x="127" y="145" width="18" height="5" rx="2" fill="#0C447C" />
              <line x1="120" y1="156" x2="152" y2="156" stroke="#0C447C" strokeWidth="1"/>
              <line x1="126" y1="160" x2="126" y2="168" stroke="#5DCAA5" strokeWidth="2" strokeLinecap="round"/>
              <line x1="132" y1="160" x2="132" y2="168" stroke="#5DCAA5" strokeWidth="2" strokeLinecap="round"/>
              <line x1="138" y1="160" x2="138" y2="168" stroke="#5DCAA5" strokeWidth="2" strokeLinecap="round"/>
              {/* Gears */}
              <g className="maint-gear-spin" transform="translate(24, 68)">
                <circle cx="0" cy="0" r="10" fill="none" stroke="#B5D4F4" strokeWidth="2"/>
                <circle cx="0" cy="0" r="4" fill="#B5D4F4"/>
                <rect x="-2" y="-13" width="4" height="6" rx="1" fill="#B5D4F4"/>
                <rect x="-2" y="7" width="4" height="6" rx="1" fill="#B5D4F4"/>
                <rect x="-13" y="-2" width="6" height="4" rx="1" fill="#B5D4F4"/>
                <rect x="7" y="-2" width="6" height="4" rx="1" fill="#B5D4F4"/>
              </g>
              <g className="maint-gear-spin-rev" transform="translate(168, 80)">
                <circle cx="0" cy="0" r="13" fill="none" stroke="#DDE8F8" strokeWidth="2.5"/>
                <circle cx="0" cy="0" r="5" fill="#DDE8F8"/>
                <rect x="-2.5" y="-17" width="5" height="7" rx="1" fill="#DDE8F8"/>
                <rect x="-2.5" y="10" width="5" height="7" rx="1" fill="#DDE8F8"/>
                <rect x="-17" y="-2.5" width="7" height="5" rx="1" fill="#DDE8F8"/>
                <rect x="10" y="-2.5" width="7" height="5" rx="1" fill="#DDE8F8"/>
              </g>
              {/* Signal */}
              <circle className="maint-pulse-ring" cx="168" cy="46" r="10" fill="none" stroke="#5DCAA5" strokeWidth="2"/>
              <circle cx="168" cy="46" r="5" fill="#5DCAA5"/>
              {/* Cloud */}
              <g opacity="0.35">
                <ellipse cx="108" cy="32" rx="18" ry="10" fill="#B5D4F4"/>
                <ellipse cx="94" cy="36" rx="12" ry="8" fill="#B5D4F4"/>
                <ellipse cx="122" cy="36" rx="12" ry="8" fill="#B5D4F4"/>
                <rect x="86" y="36" width="44" height="8" rx="2" fill="#B5D4F4"/>
              </g>
            </svg>
          </div>

          <p className="maint-eyebrow">Scheduled Maintenance</p>
          <h1 className="maint-h1">Back <em>very</em> soon.</h1>
          <p className="maint-subtitle">
            Our team is performing some upgrades to give you a better experience.
            We appreciate your patience — this won't take long.
          </p>

          <div className="maint-status-bar">
            <div className="maint-status-dot" />
            <span>Maintenance in progress &nbsp;·&nbsp; Est. completion: <strong>~2 hours</strong></span>
          </div>
        </div>
      </div>
    </>
  );
}